// A library for manipulating media queries.
// Helpful when using Aphrodite, to combine media queries from various modules.

class MediaQuery {
    private type: string;
    private features: Array<string>;

    constructor(type, features) {
        this.type = type;
        this.features = features;
    }

    // Return a new media query with this additional feature.
    and(feature) {
        return new MediaQuery(this.type, this.features.concat([feature]));
    }

    // Return two new media queries. Both have the same type and features as
    // this one, but the first will only apply to screens narrower than the
    // given width, and the second will only apply to screens at least as wide
    // as the given width.
    breakAtWidth(width) {
        return [
            this.and(`(max-width: ${width - 1}px)`),
            this.and(`(min-width: ${width}px)`),
        ];
    }

    // Return the intersection of this media query with the given media query.
    // (That is, the returned query will apply if and only if this query and
    // the given query would also apply.) Both queries must have the same type.
    intersect(other) {
        if (this.type !== other.type) {
            throw Error("cannot AND media queries of different media type");
        }

        return new MediaQuery(this.type, this.features.concat(other.features));
    }

    // Return the CSS string for this media query (i.e. `@media …`).
    // Appropriate to use as a computed property name in Aphrodite, e.g.,
    //
    // const isMobile = media.screen.and("(max-width: 400px)");
    // const styles = StyleSheet.create({
    //     header: {
    //         [isMobile]: {
    //             display: "none",
    //         },
    //     },
    // });
    toString() {
        return `@media ${this.type} and ${this.features.join(" and ")}`;
    }
}

function query(type, features) {
    return new MediaQuery(type, features);
}

const screen = query("screen", []);

export default {query, screen};
